/**
 * AliasInstanceStore — alias-row state: template ref, props,
 * overrides, hydration state.
 *
 * Template/instance model: `sheets/.docs/content/rows-aliases.md`.
 *
 * Emits through the shared `InvalidationSink` using lona-js's
 * canonical vocabulary:
 *   - prop changes  → `RowDataChanged(rowId, "props.<name>")`
 *   - override set  → `RowDataChanged(rowId, "overrides.<name>")`
 *   - detach        → `RowDataChanged(rowId, "hydrated")`
 *   - reset         → `RowDataChanged(rowId)` (root invalidation)
 */

import {
  Actor,
  type InvalidationSink,
  RowDataChanged,
} from "../tx/invalidation.ts";
import type { Json, JsonObject } from "../core/json.ts";
import { frozenCopy } from "../core/frozen-copy.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

/** One alias row's template name and props. */
export interface AliasInstance {
  readonly template: string;
  /**
   * Alias props — template binding inputs, as JSON (they persist in the
   * row's `attributes.props`). Each template reads its prop contract at
   * its edge with the `Json` readers.
   */
  readonly props: JsonObject;
  /** Instance overrides. JSON, like `props`. */
  readonly overrides: JsonObject;
  /** `true` → detached; template updates no longer propagate. */
  readonly hydrated: boolean;
}

export class AliasInstanceStore {
  static readonly ACTOR = new Actor("AliasInstanceStore");

  readonly #instanceByRow = new Map<RowLocalId, AliasInstance>();

  readonly #sink: InvalidationSink;
  constructor(sink: InvalidationSink) {
    this.#sink = sink;
  }

  /**
   * Keep `instance` as the row's, frozen, with its props and overrides
   * deeply frozen: every reader shares the one value, and each write keeps
   * a new one.
   */
  #keep(rowId: RowLocalId, instance: AliasInstance): void {
    this.#instanceByRow.set(rowId, Object.freeze({
      ...instance,
      props: frozenCopy(instance.props),
      overrides: frozenCopy(instance.overrides),
    }));
  }

  /** Read the current instance (frozen). Returns an empty default if unset. */
  get(rowId: RowLocalId): AliasInstance {
    return this.#instanceByRow.get(rowId) ?? EMPTY;
  }

  /** Seed an instance (e.g. during hydration). Does NOT emit. */
  init(rowId: RowLocalId, instance: AliasInstance): void {
    this.#keep(rowId, instance);
  }

  /** Merge prop updates. Emits one event per key that changes. */
  patchProps(rowId: RowLocalId, patch: JsonObject): void {
    const keys = Object.keys(patch);
    if (keys.length === 0) return;
    const prev = this.get(rowId);
    const mergedProps: { [key: string]: Json } = { ...prev.props };
    const changedKeys: string[] = [];
    for (const k of keys) {
      const v = patch[k];
      if (v === undefined) continue;
      if (mergedProps[k] !== v) {
        mergedProps[k] = v;
        changedKeys.push(k);
      }
    }
    if (changedKeys.length === 0) return;
    const instance: AliasInstance = { ...prev, props: mergedProps };
    this.#keep(rowId, instance);
    for (const k of changedKeys) {
      this.#sink.emit(
        new RowDataChanged(AliasInstanceStore.ACTOR, rowId, `props.${k}`),
      );
    }
  }

  /**
   * Show `props` — the row's `attributes.props` bag as the row-field ledger
   * projects it (`RowWrites`) — as the instance's props, SILENTLY (the ledger
   * announces). A row with no instance (not an alias) is left alone. Answers
   * the names of the props that changed.
   */
  showProps(rowId: RowLocalId, props: JsonObject): readonly string[] {
    const prev = this.#instanceByRow.get(rowId);
    if (prev == null || !prev.template) return [];
    const changed: string[] = [];
    for (const name of new Set([...Object.keys(prev.props), ...Object.keys(props)])) {
      const a = Object.hasOwn(prev.props, name) ? JSON.stringify(prev.props[name] ?? null) : undefined;
      const b = Object.hasOwn(props, name) ? JSON.stringify(props[name] ?? null) : undefined;
      if (a !== b) changed.push(name);
    }
    if (changed.length === 0) return [];
    this.#keep(rowId, { ...prev, props });
    return changed;
  }

  /** Set a single override. Emits. */
  setOverride(rowId: RowLocalId, attr: string, value: Json): void {
    const prev = this.get(rowId);
    if (prev.overrides[attr] === value) return;
    const instance: AliasInstance = {
      ...prev,
      overrides: { ...prev.overrides, [attr]: value },
    };
    this.#keep(rowId, instance);
    this.#sink.emit(
      new RowDataChanged(AliasInstanceStore.ACTOR, rowId, `overrides.${attr}`),
    );
  }

  /** Detach from the master. Template edits no longer propagate. */
  detach(rowId: RowLocalId): void {
    const prev = this.get(rowId);
    if (prev.hydrated) return;
    this.#keep(rowId, { ...prev, hydrated: true });
    this.#sink.emit(
      new RowDataChanged(AliasInstanceStore.ACTOR, rowId, "hydrated"),
    );
  }

  /**
   * Undo `detach`: the instance follows its master again (overrides kept) —
   * a failed fork's compensation (`Sheet.detachAlias`). Emits.
   */
  reattach(rowId: RowLocalId): void {
    const prev = this.get(rowId);
    if (!prev.hydrated) return;
    this.#keep(rowId, { ...prev, hydrated: false });
    this.#sink.emit(
      new RowDataChanged(AliasInstanceStore.ACTOR, rowId, "hydrated"),
    );
  }

  /** Reset to main component — clear overrides, un-hydrate. */
  reset(rowId: RowLocalId): void {
    const prev = this.get(rowId);
    this.#keep(rowId, {
      ...prev,
      overrides: {},
      hydrated: false,
    });
    this.#sink.emit(new RowDataChanged(AliasInstanceStore.ACTOR, rowId, null));
  }
}

const EMPTY: AliasInstance = Object.freeze({
  template: "",
  props: Object.freeze({}),
  overrides: Object.freeze({}),
  hydrated: false,
});
